import type { LocationQuery, RouteLocationRaw } from 'vue-router';

export const DEFAULT_LIST_PAGE = 1;

const LIST_PAGE_QUERY_KEY = 'page';

/**
 * Coerce a route query `page` value to a positive integer.
 * Missing or invalid values fall back to {@link DEFAULT_LIST_PAGE}.
 */
export function parseListPage(
  query: LocationQuery | Record<string, unknown> | null | undefined,
): number {
  if (!query) {
    return DEFAULT_LIST_PAGE;
  }

  const raw = query[LIST_PAGE_QUERY_KEY];
  const value = Array.isArray(raw) ? raw[0] : raw;
  const page = Number(value);

  if (!Number.isFinite(page) || page < 1 || !Number.isInteger(page)) {
    return DEFAULT_LIST_PAGE;
  }

  return page;
}

/**
 * Resolve which list page an Edit/Create form should load.
 * Create mode always uses the default; edit uses `parseListPage(query)`.
 */
export function resolveListPageForRoute(
  query: LocationQuery | Record<string, unknown> | null | undefined,
  options: { isCreate?: boolean } = {},
): number {
  if (options.isCreate) {
    return DEFAULT_LIST_PAGE;
  }

  return parseListPage(query);
}

type RouteLocationWithQuery = Extract<
  RouteLocationRaw,
  { query?: LocationQuery | Record<string, unknown> }
>;

/**
 * Merge `query.page` into a route location for list → edit navigation.
 * Always sets an explicit page so the Edit form can load that list slice.
 */
export function withListPage(
  to: RouteLocationRaw,
  page: number = DEFAULT_LIST_PAGE,
): RouteLocationRaw {
  const resolvedPage =
    Number.isFinite(page) && page >= 1 && Number.isInteger(page)
      ? page
      : DEFAULT_LIST_PAGE;

  if (typeof to === 'string') {
    return {
      path: to,
      query: {
        [LIST_PAGE_QUERY_KEY]: String(resolvedPage),
      },
    };
  }

  const location = to as RouteLocationWithQuery;
  const existingQuery =
    location.query && typeof location.query === 'object' ? location.query : {};

  return {
    ...location,
    query: {
      ...existingQuery,
      [LIST_PAGE_QUERY_KEY]: String(resolvedPage),
    },
  };
}
